import { STATUS_CONFIRMED } from '@whitelabel/helpers/constants';
import { processKeys } from '@whitelabel/xcover-shared/helpers/api';
import { KLARNA_PLUS_PARTNER_ID } from '@whitelabel/xcover-shared/helpers/constants';
import { IClaimBooking, IClaimBookingsResponse, IRCClaimBooking, IRCCustomerBooking } from '../../helpers/types';
import { getCPAPIHost } from '../../helpers/api';
import { CP_ROOT } from '../../helpers/constants';
import { claimsPortalAPI } from '.';

export const claimBookingsAPI = claimsPortalAPI.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerClaimBookings: builder.query<IClaimBookingsResponse, { id: string; locale?: string }>({
      query: ({ id, locale }) => {
        const searchParams = new URLSearchParams({
          language: locale as string,
          status: STATUS_CONFIRMED,
          view: 'claims',
        });
        return {
          url: `${getCPAPIHost({ CPBackendPercent: 0, customerID: id })}/customers/${id}/bookings/?${searchParams}`,
          headers: {
            'X-CP-ROOT': CP_ROOT,
          },
        };
      },
      transformResponse: (response: IClaimBookingsResponse) => {
        if (!response.results) {
          return { results: processKeys(response) } as IClaimBookingsResponse;
        }
        return processKeys(response) as IClaimBookingsResponse;
      },
    }),
    getRCCustomerClaimBookings: builder.query<IRCCustomerBooking[], { customerID: number; locale: string }>({
      query: ({ customerID, locale }) => {
        const searchParams = new URLSearchParams({
          language: locale,
        });
        return `${getCPAPIHost()}/customers/${customerID}/bookings/?${searchParams}`;
      },
    }),
    getClaimBooking: builder.query<IClaimBooking, { bookingID?: string; locale?: string; security_token?: string }>({
      query: ({ bookingID, locale, security_token }) => {
        const searchParams = new URLSearchParams({
          ...(locale && { language: locale }),
          view: 'claims',
        });
        if (security_token) {
          searchParams.append('security_token', security_token);
        }

        return `${getCPAPIHost({ id: bookingID, CPBackendPercent: 1 })}/bookings/${bookingID}/?${searchParams}`;
      },
      transformResponse: (response: any): IClaimBooking => {
        const transformedResponse = processKeys(response) as IClaimBooking;
        const quotesDropdown = transformedResponse.quotes
          .filter(({ status }) => status === STATUS_CONFIRMED)
          .map(
            ({
              id: quoteIDValue,
              quoteTitle,
              policy: {
                content: { title },
                policyName,
                policyType,
              },
              claimTypes,
            }) => {
              const { partner } = transformedResponse;
              let label = `${title || policyName} - ${quoteTitle}`;
              if (partner.slug === 'revolut' || partner.id === KLARNA_PLUS_PARTNER_ID) {
                label = `${quoteTitle} - ${title || policyName}`;
              }
              return {
                value: quoteIDValue,
                label,
                policyType,
                claimTypes,
              };
            },
          );
        return {
          ...transformedResponse,
          quotesDropdown,
        };
      },
    }),
    getRCClaimBooking: builder.query<IRCClaimBooking, { bookingID?: string; locale?: string }>({
      query: ({ bookingID, locale }) => {
        const searchParams = new URLSearchParams({
          ...(locale && { language: locale }),
        });
        return `${getCPAPIHost({ CPBackendPercent: 1 })}/bookings/${bookingID}/?${searchParams}`;
      },
      transformResponse: (response: any): IRCClaimBooking => {
        const transformedResponse = processKeys(response) as IRCClaimBooking;
        const quotesDropdown = transformedResponse.quotes
          .filter(({ status }) => status.toUpperCase() === STATUS_CONFIRMED)
          .map(
            ({
              id: quoteIDValue,
              policy: {
                content: { title },
                policyName,
              },
              claimTypeSelector,
            }) => ({
              value: quoteIDValue,
              label: `${title || policyName}`,
              claimTypeSelector,
            }),
          );
        return {
          ...transformedResponse,
          quotesDropdown,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCustomerClaimBookingsQuery,
  useLazyGetCustomerClaimBookingsQuery,
  useGetRCCustomerClaimBookingsQuery,
  useLazyGetClaimBookingQuery,
  useGetClaimBookingQuery,
  useGetRCClaimBookingQuery,
} = claimBookingsAPI;
